import React, { useState } from 'react';
import { Button, Row, Col } from 'antd';
import { motion } from 'framer-motion';
import { ArrowRight, MessageCircle, LogIn } from 'lucide-react';
import SignupModal from './SignupModal';
import WhatsAppInterestModal from './WhatsAppInterestModal';
import { Trans } from 'react-i18next';
import { track } from '../analytics';
import { useTranslation } from 'react-i18next';

const Hero = () => {
  const [modalOpen, setModalOpen] = useState(false);
  const [whatsappOpen, setWhatsAppOpen] = useState(false);
  const { t } = useTranslation();

  return (
    <div className="relative min-h-screen flex items-center justify-center text-white overflow-hidden" style={{backgroundImage: 'url(/assets/hero-bg.png)', backgroundSize: 'cover', backgroundPosition: 'center'}}>
      <div className="absolute inset-0 bg-gradient-to-br from-black/60 via-black/40 to-primary/20 z-10"></div>
      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent z-10"></div>
      
      {/* Floating elements */}
      <div className="absolute top-20 left-10 w-2 h-2 bg-green-400 rounded-full animate-pulse z-20"></div>
      <div className="absolute top-40 right-20 w-1 h-1 bg-white/60 rounded-full animate-ping z-20"></div>
      <div className="absolute bottom-32 left-20 w-1.5 h-1.5 bg-primary rounded-full animate-pulse z-20"></div>
      
      <div className="container mx-auto px-6 z-30">
        <Row justify="center" align="middle" className="min-h-screen">
          <Col xs={24} lg={18} className="text-center">
            <motion.div
              initial={{ opacity: 0, y: 60 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1, ease: "easeOut" }}
            >
              <motion.div 
                className="inline-flex items-center gap-3 px-6 py-2 bg-white/10 backdrop-blur-sm rounded-full border border-white/20 mb-8"
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.3, duration: 0.6 }}
              >
                <span className="text-sm font-medium text-green-300">{t('hero.badge')}</span>
                <span className="w-1 h-1 rounded-full bg-white/40" aria-hidden="true" />
                <a
                  href="https://app.fieldops.agcurate.com/app/"
                  className="inline-flex items-center gap-1.5 text-sm font-semibold px-3 py-1 rounded-full bg-white/15 hover:bg-white/25 text-white shadow-sm border border-white/20 transition-colors"
                >
                  <LogIn size={14} className="opacity-90" />
                  <span>{t('common.login')}</span>
                </a>
              </motion.div>
              
              <h1 className="text-4xl md:text-6xl lg:text-7xl font-black mb-6 leading-tight">
                <span className="bg-gradient-to-r from-white via-green-100 to-green-200 bg-clip-text text-transparent">
                  {t('hero.headline1')}
                </span>
                <br />
                <span className="text-white/90 font-light text-3xl md:text-4xl lg:text-5xl">
                  {t('hero.headline2')}
                </span>
              </h1>
              
              <motion.h2 
                className="text-xl md:text-2xl mb-6 text-gray-200 font-light max-w-3xl mx-auto leading-relaxed"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.6, duration: 0.8 }}
              >
                {t('hero.subhead')}
              </motion.h2>
              
              <motion.div 
                className="mb-10"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.8, duration: 0.8 }}
              >
                <p className="text-lg md:text-xl font-medium">
                  <Trans
                    i18nKey="hero.inspectExpect"
                    components={{
                      ins: <span className="px-2 py-1 bg-green-500/20 text-green-300 rounded font-bold" />,
                      exp: <span className="px-2 py-1 bg-red-500/20 text-red-300 rounded font-bold" />,
                    }}
                  />
                </p>
              </motion.div>
              
              <motion.div 
                className="flex flex-col sm:flex-row gap-4 justify-center items-center"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 1, duration: 0.8 }}
              >
                <Button 
                  type="primary" 
                  size="large" 
                  className="bg-gradient-to-r from-primary to-green-600 border-none hover:from-green-600 hover:to-primary shadow-2xl shadow-primary/25 px-8 py-6 h-auto text-lg font-semibold rounded-xl group transition-all duration-300 hover:scale-105"
                  icon={<ArrowRight className="group-hover:translate-x-1 transition-transform" size={20} />}
                  iconPosition="end"
                  onClick={() => { setModalOpen(true); track('cta_start_free_trial_click'); }}
                >
                  {t('common.startFreeTrial')}
                </Button>
                <motion.div
                  animate={{ y: [0, -6, 0], boxShadow: ['0 10px 30px rgba(255,255,255,0.12)', '0 20px 50px rgba(255,255,255,0.25)', '0 10px 30px rgba(255,255,255,0.12)'] }}
                  transition={{ duration: 2.1, repeat: Infinity, ease: 'easeInOut' }}
                  className="relative"
                >
                  <motion.span
                    aria-hidden="true"
                    className="pointer-events-none absolute -inset-2 rounded-2xl border border-white/30"
                    animate={{ opacity: [0.2, 0.6, 0.2], scale: [1, 1.04, 1] }}
                    transition={{ duration: 2.1, repeat: Infinity, ease: 'easeInOut' }}
                  />
                  <Button 
                    size="large" 
                    className="relative overflow-hidden bg-gradient-to-r from-white via-white to-green-50 text-dark border-none hover:from-white hover:to-green-100 px-8 py-6 h-auto text-lg font-semibold rounded-xl group transition-all duration-300 hover:scale-105"
                    icon={<MessageCircle className="group-hover:scale-110 transition-transform" size={18} />}
                    onClick={() => { setWhatsAppOpen(true); track('cta_whatsapp_early_access_click'); }}
                  >
                    <motion.span
                      aria-hidden="true"
                      className="pointer-events-none absolute -inset-6 opacity-0 group-hover:opacity-100"
                      style={{ background: 'linear-gradient(110deg, rgba(255,255,255,0) 30%, rgba(34,197,94,0.35) 50%, rgba(255,255,255,0) 70%)' }}
                      animate={{ x: ['-60%', '120%'] }}
                      transition={{ duration: 1.6, repeat: Infinity, ease: 'easeInOut' }}
                    />
                    <span className="relative z-10 inline-flex items-center gap-2">
                      {t('common.whatsappCta')}
                      <motion.span
                        aria-hidden="true"
                        className="inline-flex h-2.5 w-2.5 rounded-full bg-green-500 shadow-[0_0_12px_rgba(34,197,94,0.8)]"
                        animate={{ scale: [1, 1.35, 1], opacity: [0.7, 1, 0.7] }}
                        transition={{ duration: 1.6, repeat: Infinity, ease: 'easeInOut' }}
                      />
                    </span>
                  </Button>
                </motion.div>
              </motion.div>
              
              <motion.div 
                className="mt-12 text-sm text-white/60"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 1.2, duration: 0.8 }}
              >
                <p>✨ {t('hero.noCard')}</p>
              </motion.div>
            </motion.div>
          </Col>
        </Row>
      </div>
      
      <SignupModal 
        open={modalOpen} 
        onClose={() => setModalOpen(false)} 
      />
      <WhatsAppInterestModal
        open={whatsappOpen}
        onClose={() => setWhatsAppOpen(false)}
      />
    </div>
  );
};

export default Hero;
